import React from 'react';

type ConfirmTone = 'danger' | 'cyan' | 'neutral';

interface ConfirmModalProps {
  title: string;
  subtitle?: string;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
  loading?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

const toneStyles: Record<ConfirmTone, React.CSSProperties> = {
  danger: { 
    '--btn-bg': 'var(--danger-bg-subtle)', 
    '--btn-bg-hover': 'var(--danger-bg-subtle)',
    '--btn-border': 'var(--danger-border-subtle)', 
    '--btn-text': 'var(--danger)'
  } as React.CSSProperties,
  cyan: { 
    '--btn-bg': 'var(--accent)', 
    '--btn-bg-hover': 'var(--accent-hover)',
    '--btn-border': 'var(--accent)', 
    '--btn-text': 'var(--fg-on-accent)'
  } as React.CSSProperties,
  neutral: { 
    '--btn-bg': 'var(--bg-interactive-idle)', 
    '--btn-bg-hover': 'var(--bg-interactive-hover)',
    '--btn-border': 'var(--border-default)', 
    '--btn-text': 'var(--fg-secondary)'
  } as React.CSSProperties
};

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  subtitle,
  onConfirm,
  onCancel,
  loading,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  icon,
  children
}) => {
  const styles = toneStyles[tone];

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onCancel();
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop"
      onClick={handleBackgroundClick}
    >
      <div className="w-full max-w-md holographic-card">
        <div 
          className="p-6 border-b"
          style={{ 
            borderBottomColor: 'var(--border-subtle)',
            padding: 'var(--space-8)'
          }}
        >
          <h2 
            className="text-lg font-mono uppercase tracking-wider"
            style={{ 
              color: 'var(--accent)',
              fontSize: 'var(--font-size-lg)',
              fontFamily: 'var(--font-family-mono)',
              letterSpacing: 'var(--letter-spacing-wider)'
            }}
          >
            {title}
          </h2>
          {subtitle && (
            <p 
              className="text-xs font-mono mt-1"
              style={{
                color: 'var(--fg-muted)',
                fontSize: 'var(--font-size-xs)',
                fontFamily: 'var(--font-family-mono)',
                marginTop: 'var(--space-1)'
              }}
            >
              {subtitle}
            </p>
          )}
        </div>

        <div style={{ padding: 'var(--space-8)' }}>
          {children && (
            <div 
              className="flex items-start mb-6"
              style={{ gap: 'var(--space-4)', marginBottom: 'var(--space-8)' }}
            >
              {icon && <div style={{ marginTop: 'var(--space-1)' }}>{icon}</div>}
              <div 
                className="font-mono text-sm"
                style={{ 
                  color: 'var(--fg-secondary)', 
                  fontFamily: 'var(--font-family-mono)', 
                  fontSize: 'var(--font-size-sm)' 
                }}
              >
                {children}
              </div>
            </div>
          )}
          <div className="flex" style={{ gap: 'var(--space-4)' }}>
            <button
              type="button"
              onClick={onCancel}
              className="confirm-cancel-btn flex-1 font-mono text-sm uppercase tracking-wider transition-all"
              style={{
                padding: 'var(--space-2) var(--space-5)',
                backgroundColor: 'transparent',
                border: `1px solid var(--border-subtle)`,
                borderRadius: 'var(--radius-lg)',
                color: 'var(--fg-muted)',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                letterSpacing: 'var(--letter-spacing-wider)',
                transition: `all var(--motion-duration-fast) var(--motion-ease-standard)`
              }}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={() => onConfirm()}
              disabled={!!loading}
              className="flex-1 font-mono text-sm uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              style={{
                ...styles,
                padding: 'var(--space-2) var(--space-5)',
                backgroundColor: 'var(--btn-bg)',
                border: `1px solid var(--btn-border)`,
                borderRadius: 'var(--radius-lg)',
                color: 'var(--btn-text)',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                letterSpacing: 'var(--letter-spacing-wider)',
                transition: `all var(--motion-duration-fast) var(--motion-ease-standard)`,
                gap: 'var(--space-2)'
              }}
            >
              {loading ? (
                <>
                  <div 
                    className="border rounded-full animate-spin"
                    style={{
                      width: 'var(--space-5)', 
                      height: 'var(--space-5)', 
                      borderColor: 'currentColor',
                      borderTopColor: 'transparent'
                    }} 
                  />
                  Working...
                </>
              ) : (
                confirmLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;


